import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Slider,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  IconArrowLeft,
  IconUserPlus,
  IconEdit,
  IconTrash,
  IconPlayerPlay,
  IconPlayerStop,
  IconVolume,
  IconSettings,
  IconPhone,
  IconMusic,
  IconMessage,
  IconGripVertical,
  IconChevronDown,
  IconSearch
} from '@tabler/icons-react';
import { useQueue } from '../contexts/QueueContext';
import { useSipNumber } from '../contexts/SipNumberContext';
import { useMusicFile } from '../contexts/MusicFileContext';

// Component Configuration Component
const ComponentConfig: React.FC<{
  component: IvrComponent;
  onConfigChange: (config: any) => void;
  queues: any[];
  musicFiles: any[];
  onUploadMusicFile: (file: File) => Promise<any>;
  musicLoading: boolean;
}> = ({ component, onConfigChange, queues, musicFiles, onUploadMusicFile, musicLoading }) => {
  const [config, setConfig] = useState(component.config);

  const handleConfigChange = (field: string, value: any) => {
    const newConfig = { ...config, [field]: value };
    setConfig(newConfig);
    onConfigChange(newConfig);
  };

  const renderConfig = () => {
    switch (component.type) {
      case 'welcome':
      case 'message':
        return (
          <TextField
            fullWidth
            label="Mesaj"
            value={config.message || ''}
            onChange={(e) => handleConfigChange('message', e.target.value)}
            multiline
            rows={3}
          />
        );

      case 'menu':
        return (
          <Box>
            <TextField
              fullWidth
              label="Menü Mesajı"
              value={config.message || ''}
              onChange={(e) => handleConfigChange('message', e.target.value)}
              multiline
              rows={2}
              sx={{ mb: 2 }}
            />
            <Typography variant="subtitle2" gutterBottom>
              Seçenekler:
            </Typography>
            {config.options?.map((option: any, index: number) => (
              <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <TextField
                  size="small"
                  label="Tuş"
                  value={option.key}
                  onChange={(e) => {
                    const newOptions = [...config.options];
                    newOptions[index].key = e.target.value;
                    handleConfigChange('options', newOptions);
                  }}
                  sx={{ width: 80 }}
                />
                <TextField
                  size="small"
                  label="Etiket"
                  value={option.label}
                  onChange={(e) => {
                    const newOptions = [...config.options];
                    newOptions[index].label = e.target.value;
                    handleConfigChange('options', newOptions);
                  }}
                  sx={{ flexGrow: 1 }}
                />
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Hedef</InputLabel>
                  <Select
                    value={option.target || ''}
                    onChange={(e) => {
                      const newOptions = [...config.options];
                      newOptions[index].target = e.target.value;
                      handleConfigChange('options', newOptions);
                    }}
                  >
                    {queues.map((queue) => (
                      <MenuItem key={queue._id} value={queue._id}>
                        {queue.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            ))}
          </Box>
        );

      case 'queue':
        return (
          <Box>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Kuyruk</InputLabel>
              <Select
                value={config.queueId || ''}
                onChange={(e) => handleConfigChange('queueId', e.target.value)}
              >
                {queues.length === 0 ? (
                  <MenuItem disabled>
                    <em>Kuyruk bulunamadı</em>
                  </MenuItem>
                ) : (
                  queues.map((queue) => (
                    <MenuItem key={queue._id} value={queue._id}>
                      {queue.name}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
            {queues.length === 0 && (
              <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                Kuyruk bulunamadı. Lütfen önce kuyruk oluşturun.
              </Typography>
            )}
            
            <Typography variant="subtitle2" gutterBottom>
              Bekleme Müziği (MP3)
            </Typography>
            <Box sx={{ mb: 2 }}>
              <input
                type="file"
                accept=".mp3,.wav,.ogg"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    const uploadedFile = await onUploadMusicFile(file);
                    if (uploadedFile) {
                      handleConfigChange('musicFileId', uploadedFile._id);
                    }
                  }
                }}
                style={{ display: 'none' }}
                id="music-upload"
              />
              <label htmlFor="music-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<IconMusic size={20} />}
                  sx={{ mb: 1 }}
                  disabled={musicLoading}
                >
                  {musicLoading ? 'Yükleniyor...' : 'Müzik Dosyası Seç'}
                </Button>
              </label>
              
              {/* Mevcut müzik dosyaları listesi */}
              {musicFiles.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Mevcut Müzik Dosyaları:
                  </Typography>
                  <FormControl fullWidth>
                    <InputLabel>Müzik Dosyası Seç</InputLabel>
                    <Select
                      value={config.musicFileId || ''}
                      onChange={(e) => handleConfigChange('musicFileId', e.target.value)}
                    >
                      <MenuItem value="">
                        <em>Müzik dosyası seçin</em>
                      </MenuItem>
                      {musicFiles.map((file: any) => (
                        <MenuItem key={file._id} value={file._id}>
                          🎵 {file.originalName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              )}
              
              {config.musicFileId && (
                <Box sx={{ mt: 1, p: 2, border: '1px dashed #ccc', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    🎵 {musicFiles.find((f: any) => f._id === config.musicFileId)?.originalName}
                  </Typography>
                  <Button
                    size="small"
                    color="error"
                    onClick={() => handleConfigChange('musicFileId', '')}
                    sx={{ mt: 1 }}
                  >
                    Kaldır
                  </Button>
                </Box>
              )}
            </Box>
            
            <TextField
              fullWidth
              label="Bekleme Mesajı"
              value={config.message || ''}
              onChange={(e) => handleConfigChange('message', e.target.value)}
              multiline
              rows={2}
              placeholder="Örnek: Lütfen bekleyin, en kısa sürede size dönüş yapacağız..."
            />
          </Box>
        );

      case 'wait':
        return (
          <TextField
            fullWidth
            label="Bekleme Mesajı"
            value={config.message || ''}
            onChange={(e) => handleConfigChange('message', e.target.value)}
            multiline
            rows={2}
          />
        );

      case 'tts':
        return (
          <Box>
            <TextField
              fullWidth
              label="Metin"
              value={config.text || ''}
              onChange={(e) => handleConfigChange('text', e.target.value)}
              multiline
              rows={4}
              sx={{ mb: 2 }}
            />
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Ses</InputLabel>
              <Select
                value={config.voice || 'tr-TR'}
                onChange={(e) => handleConfigChange('voice', e.target.value)}
              >
                <MenuItem value="tr-TR">Türkçe (Kadın)</MenuItem>
                <MenuItem value="tr-TR-Male">Türkçe (Erkek)</MenuItem>
                <MenuItem value="en-US">İngilizce (Kadın)</MenuItem>
                <MenuItem value="en-US-Male">İngilizce (Erkek)</MenuItem>
              </Select>
            </FormControl>
            <Typography gutterBottom>Hız: {config.speed || 1.0}</Typography>
            <Slider
              value={config.speed || 1.0}
              onChange={(_, value) => handleConfigChange('speed', value)}
              min={0.5}
              max={2.0}
              step={0.1}
              marks
              valueLabelDisplay="auto"
            />
          </Box>
        );

      case 'loop':
        return (
          <Box>
            <TextField
              fullWidth
              label="Bekleme Süresi (saniye)"
              type="number"
              value={config.duration || 5}
              onChange={(e) => handleConfigChange('duration', parseInt(e.target.value))}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Bekleme Mesajı"
              value={config.message || ''}
              onChange={(e) => handleConfigChange('message', e.target.value)}
              multiline
              rows={2}
            />
          </Box>
        );

      case 'hangup':
        return (
          <TextField
            fullWidth
            label="Veda Mesajı"
            value={config.message || ''}
            onChange={(e) => handleConfigChange('message', e.target.value)}
            multiline
            rows={2}
          />
        );

      default:
        return <Typography>Bu bileşen için yapılandırma yok</Typography>;
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      {renderConfig()}
    </Box>
  );
};

interface IvrFlow {
  _id?: string;
  name: string;
  description: string;
  sipNumberId: string;
  isActive: boolean;
  components: IvrComponent[];
  startComponentId: string;
}

interface IvrComponent {
  _id?: string;
  id: string;
  type: 'welcome' | 'menu' | 'queue' | 'message' | 'hangup' | 'wait' | 'loop' | 'tts';
  name: string;
  position: { x: number; y: number };
  config: any;
  connections: string[]; // Connected component IDs
}

interface ComponentTemplate {
  type: string;
  name: string;
  icon: React.ReactNode;
  color: string;
  description: string;
}

const IvrBuilder: React.FC = () => {
  const [ivrFlows, setIvrFlows] = useState<IvrFlow[]>([]);
  const [selectedFlow, setSelectedFlow] = useState<IvrFlow | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingFlow, setEditingFlow] = useState<IvrFlow | null>(null);
  const [formData, setFormData] = useState<IvrFlow>({
    name: '',
    description: '',
    sipNumberId: '',
    isActive: true,
    components: [],
    startComponentId: ''
  });

  const [draggedComponent, setDraggedComponent] = useState<ComponentTemplate | null>(null);
  const [selectedComponent, setSelectedComponent] = useState<IvrComponent | null>(null);
  const [componentConfigOpen, setComponentConfigOpen] = useState(false);
  const [draggedFlowComponent, setDraggedFlowComponent] = useState<IvrComponent | null>(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  const queueContext = useQueue();
  const sipNumberContext = useSipNumber();
  const musicFileContext = useMusicFile();

  // Component templates
  const componentTemplates: ComponentTemplate[] = [
    {
      type: 'welcome',
      name: 'Hoş Geldin',
      icon: <IconPhone size={20} />,
      color: '#4CAF50',
      description: 'Arayanı karşılayan ilk mesaj'
    },
    {
      type: 'menu',
      name: 'Menü',
      icon: <IconSettings size={20} />,
      color: '#2196F3',
      description: 'Seçenekler sunan menü'
    },
    {
      type: 'queue',
      name: 'Kuyruk',
      icon: <IconMusic size={20} />,
      color: '#FF9800',
      description: 'Arayanı kuyruğa yönlendir'
    },
    {
      type: 'message',
      name: 'Mesaj',
      icon: <IconMessage size={20} />,
      color: '#9C27B0',
      description: 'Özel mesaj oku'
    },
    {
      type: 'tts',
      name: 'Metin-Ses',
      icon: <IconVolume size={20} />,
      color: '#E91E63',
      description: 'Metni sese çevir'
    },
    {
      type: 'wait',
      name: 'Bekle',
      icon: <IconPlayerStop size={20} />,
      color: '#607D8B',
      description: 'Belirli süre bekle'
    },
    {
      type: 'loop',
      name: 'Döngü',
      icon: <IconPlayerPlay size={20} />,
      color: '#795548',
      description: 'Başa dön'
    },
    {
      type: 'hangup',
      name: 'Kapat',
      icon: <IconPhone size={20} />,
      color: '#F44336',
      description: 'Aramayı sonlandır'
    }
  ];

  // Fetch IVR flows
  const fetchIvrFlows = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch('/api/ivr-flows', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setIvrFlows(data.data || []);
      } else {
        setError('IVR akışları yüklenemedi');
      }
    } catch (err) {
      setError('IVR akışları yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIvrFlows();
    // SIP numaralarını yükle
    if (sipNumberContext.sipNumbers.length === 0) {
      sipNumberContext.fetchSipNumbers();
    }
    // Kuyrukları yükle
    if (queueContext.queues.length === 0) {
      queueContext.fetchQueues();
    }
    // Müzik dosyalarını yükle
    if (musicFileContext.musicFiles.length === 0) {
      musicFileContext.fetchMusicFiles();
    }
  }, []);

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const url = editingFlow ? `/api/ivr-flows/${editingFlow._id}` : '/api/ivr-flows';
      const method = editingFlow ? 'PUT' : 'POST';

      // selectedFlow'u kullan, eğer yoksa formData'yı kullan
      const dataToSend = selectedFlow ? {
        ...formData,
        components: selectedFlow.components
      } : formData;

      console.log('🔍 IVR Builder: Gönderilen veri:', dataToSend);
      console.log('🔍 IVR Builder: formData.sipNumberId:', formData.sipNumberId);

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(dataToSend)
      });

      if (response.ok) {
        setSuccess(editingFlow ? 'IVR akışı güncellendi' : 'IVR akışı oluşturuldu');
        setDialogOpen(false);
        setEditingFlow(null);
        resetForm();
        fetchIvrFlows();
      } else {
        setError('IVR akışı kaydedilemedi');
      }
    } catch (err) {
      setError('IVR akışı kaydedilemedi');
    } finally {
      setLoading(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      sipNumberId: '',
      isActive: true,
      components: [],
      startComponentId: ''
    });
  };

  // Open dialog for new flow
  const handleNewFlow = () => {
    resetForm();
    setEditingFlow(null);
    setDialogOpen(true);
  };

  // Open dialog for editing
  const handleEditFlow = (flow: IvrFlow) => {
    setFormData(flow);
    setEditingFlow(flow);
    setDialogOpen(true);
  };

  // Delete flow
  const handleDeleteFlow = async (id: string) => {
    if (!window.confirm('Bu IVR akışını silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/ivr-flows/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setSuccess('IVR akışı silindi');
        fetchIvrFlows();
      } else {
        setError('IVR akışı silinemedi');
      }
    } catch (err) {
      setError('IVR akışı silinemedi');
    } finally {
      setLoading(false);
    }
  };

  // Toggle flow active status
  const handleToggleActive = async (flow: IvrFlow) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/ivr-flows/${flow._id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ...flow, isActive: !flow.isActive })
      });

      if (response.ok) {
        setSuccess(`IVR akışı ${!flow.isActive ? 'aktif' : 'pasif'} edildi`);
        fetchIvrFlows();
      } else {
        setError('IVR akışı güncellenemedi');
      }
    } catch (err) {
      setError('IVR akışı güncellenemedi');
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, template: ComponentTemplate) => {
    setDraggedComponent(template);
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (!draggedComponent || !selectedFlow) return;

    // Calculate position based on component count (vertical flow)
    const componentCount = selectedFlow.components.length;
    const x = 50; // Fixed horizontal position
    const y = componentCount * 120 + 50; // Vertical spacing

    const newComponent: IvrComponent = {
      id: `comp_${Date.now()}`,
      type: draggedComponent.type as any,
      name: draggedComponent.name,
      position: { x, y },
      config: getDefaultConfig(draggedComponent.type),
      connections: []
    };

    const updatedFlow = {
      ...selectedFlow,
      components: [...selectedFlow.components, newComponent]
    };

    setSelectedFlow(updatedFlow);
    setDraggedComponent(null);
  };

  // Get default config for component type
  const getDefaultConfig = (type: string) => {
    switch (type) {
      case 'welcome':
        return { message: 'Merhaba, size nasıl yardımcı olabilirim?' };
      case 'menu':
        return { 
          message: 'Lütfen bir seçenek belirtin',
          options: [
            { key: '1', label: 'Satış', target: '' },
            { key: '2', label: 'Destek', target: '' },
            { key: '3', label: 'Fatura', target: '' }
          ]
        };
      case 'queue':
        return { queueId: '', musicFileId: '', message: 'Temsilciye bağlanıyorum...' };
      case 'message':
        return { message: 'Mesajınız' };
      case 'tts':
        return { text: 'Metni buraya yazın', voice: 'tr-TR', speed: 1.0 };
      case 'wait':
        return { duration: 5, message: 'Lütfen bekleyin...' };
      case 'loop':
        return { targetComponentId: '' };
      case 'hangup':
        return { message: 'İyi günler!' };
      default:
        return {};
    }
  };

  // Handle component click
  const handleComponentClick = (component: IvrComponent) => {
    setSelectedComponent(component);
    setComponentConfigOpen(true);
  };

  // Get component color by type
  const getComponentColor = (type: string) => {
    const template = componentTemplates.find(t => t.type === type);
    return template?.color || '#666';
  };

  // Get component type name
  const getComponentTypeName = (type: string) => {
    const template = componentTemplates.find(t => t.type === type);
    return template?.name || type;
  };

  // Reorder components vertically
  const handleReorderComponents = () => {
    if (!selectedFlow) return;
    
    const reorderedComponents = selectedFlow.components.map((component, index) => ({
      ...component,
      position: { x: 50, y: index * 120 + 50 }
    }));
    
    setSelectedFlow({
      ...selectedFlow,
      components: reorderedComponents
    });
  };

  // Clear all components
  const handleClearFlow = () => {
    if (!selectedFlow) return;
    
    setSelectedFlow({
      ...selectedFlow,
      components: []
    });
  };

  // Handle flow component drag start
  const handleFlowComponentDragStart = (e: React.DragEvent, component: IvrComponent) => {
    e.stopPropagation();
    setDraggedFlowComponent(component);
    
    const rect = (e.target as HTMLElement).getBoundingClientRect();
    const canvasRect = (e.currentTarget.closest('.flow-canvas') as HTMLElement)?.getBoundingClientRect();
    if (canvasRect) {
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
    }
  };

  // Handle flow component drag end
  const handleFlowComponentDragEnd = (e: React.DragEvent) => {
    if (!draggedFlowComponent || !selectedFlow) return;
    
    const canvas = e.currentTarget.closest('.flow-canvas') as HTMLElement;
    if (!canvas) return;
    
    const canvasRect = canvas.getBoundingClientRect();
    const newX = e.clientX - canvasRect.left - dragOffset.x;
    const newY = e.clientY - canvasRect.top - dragOffset.y;
    
    // Update component position
    const updatedComponents = selectedFlow.components.map(comp =>
      comp.id === draggedFlowComponent.id
        ? { ...comp, position: { x: Math.max(0, newX), y: Math.max(0, newY) } }
        : comp
    );
    
    setSelectedFlow({
      ...selectedFlow,
      components: updatedComponents
    });
    
    setDraggedFlowComponent(null);
    setDragOffset({ x: 0, y: 0 });
  };

  // Update component config
  const updateComponentConfig = (config: any) => {
    if (!selectedComponent || !selectedFlow) return;

    const updatedComponents = selectedFlow.components.map(comp =>
      comp.id === selectedComponent.id ? { ...comp, config } : comp
    );

    setSelectedFlow({
      ...selectedFlow,
      components: updatedComponents
    });

    setSelectedComponent({
      ...selectedComponent,
      config
    });
  };

  // Save flow
  const saveFlow = async () => {
    if (!selectedFlow) return;

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/ivr-flows/${selectedFlow._id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(selectedFlow)
      });

      if (response.ok) {
        setSuccess('IVR akışı kaydedildi');
        fetchIvrFlows();
      } else {
        setError('IVR akışı kaydedilemedi');
      }
    } catch (err) {
      setError('IVR akışı kaydedilemedi');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      p: 0
    }}>
      {/* Header */}
      <Box sx={{
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(20px)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
        p: 3,
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
      }}>
        <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '24px'
              }}>
                🎯
              </Box>
              <Box>
                <Typography variant="h4" sx={{ 
                  fontWeight: 700, 
                  background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 0.5
                }}>
                  IVR Akış Oluşturucu
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Görsel IVR akışları tasarlayın ve yönetin
                </Typography>
              </Box>
            </Box>
            
            <Button
              variant="contained"
              startIcon={<IconUserPlus size={20} />}
              onClick={handleNewFlow}
              sx={{
                background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
                boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                px: 3,
                py: 1.5,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 20px rgba(102, 126, 234, 0.6)',
                }
              }}
            >
              ✨ Yeni IVR Akışı
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Main Content */}
      <Box sx={{ maxWidth: 1400, mx: 'auto', p: 3 }}>

        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 3, 
              borderRadius: 2,
              boxShadow: '0 4px 12px rgba(244, 67, 54, 0.2)',
              border: 'none'
            }} 
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert 
            severity="success" 
            sx={{ 
              mb: 3, 
              borderRadius: 2,
              boxShadow: '0 4px 12px rgba(76, 175, 80, 0.2)',
              border: 'none'
            }} 
            onClose={() => setSuccess(null)}
          >
            {success}
          </Alert>
        )}

      {loading && <CircularProgress sx={{ mb: 2 }} />}

        <Grid container spacing={4}>
          {/* Flow List */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Card sx={{
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
              border: 'none',
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              height: 'fit-content'
            }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ 
                  fontWeight: 600, 
                  mb: 3,
                  color: '#2c3e50',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}>
                  📋 Akış Listesi
                </Typography>
                <List sx={{ p: 0 }}>
                  {ivrFlows.map((flow) => (
                    <ListItem
                      key={flow._id}
                      onClick={() => setSelectedFlow(flow)}
                      sx={{
                        borderRadius: 2,
                        mb: 1,
                        background: selectedFlow?._id === flow._id 
                          ? 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)' 
                          : 'transparent',
                        color: selectedFlow?._id === flow._id ? 'white' : 'inherit',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        border: '1px solid transparent',
                        '&:hover': {
                          background: selectedFlow?._id === flow._id 
                            ? 'linear-gradient(45deg, #5a6fd8 30%, #6a4190 90%)'
                            : 'rgba(102, 126, 234, 0.1)',
                          transform: 'translateX(4px)',
                          border: '1px solid rgba(102, 126, 234, 0.2)'
                        }
                      }}
                    >
                      <ListItemText
                        primary={
                          <Typography variant="subtitle2" fontWeight={600}>
                            {flow.name}
                          </Typography>
                        }
                        secondary={
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="caption" display="block" sx={{ 
                              opacity: 0.8,
                              mb: 1
                            }}>
                              {flow.components.length} bileşen
                            </Typography>
                            <Chip
                              label={flow.isActive ? 'Aktif' : 'Pasif'}
                              size="small"
                              sx={{
                                background: flow.isActive 
                                  ? 'rgba(76, 175, 80, 0.2)' 
                                  : 'rgba(158, 158, 158, 0.2)',
                                color: flow.isActive ? '#4caf50' : '#9e9e9e',
                                fontWeight: 600,
                                border: 'none'
                              }}
                            />
                          </Box>
                        }
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditFlow(flow);
                          }}
                          sx={{
                            color: selectedFlow?._id === flow._id ? 'white' : '#1976d2',
                            '&:hover': {
                              background: 'rgba(25, 118, 210, 0.1)'
                            }
                          }}
                        >
                          <IconEdit size={20} />
                        </IconButton>
                        <IconButton
                          edge="end"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteFlow(flow._id!);
                          }}
                          sx={{
                            color: selectedFlow?._id === flow._id ? 'white' : '#f44336',
                            '&:hover': {
                              background: 'rgba(244, 67, 54, 0.1)'
                            }
                          }}
                        >
                          <IconTrash size={20} />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>

          {/* IVR Builder */}
          <Grid size={{ xs: 12, md: 8 }}>
            <Card sx={{
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
              border: 'none',
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              minHeight: '80vh'
            }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center', 
                  mb: 3,
                  pb: 2,
                  borderBottom: '2px solid #f5f5f5'
                }}>
                  <Box>
                    <Typography variant="h5" sx={{ 
                      fontWeight: 700,
                      color: '#2c3e50',
                      mb: 0.5
                    }}>
                      {selectedFlow ? selectedFlow.name : '🎨 IVR Akışı Seçin'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedFlow ? 'Akışınızı tasarlayın ve düzenleyin' : 'Başlamak için sol taraftan bir akış seçin'}
                    </Typography>
                  </Box>
                  {selectedFlow && (
                    <Button
                      variant="contained"
                      onClick={saveFlow}
                      disabled={loading}
                      sx={{
                        background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
                        boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                        px: 3,
                        py: 1.5,
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 6px 20px rgba(102, 126, 234, 0.6)',
                        }
                      }}
                    >
                      💾 Kaydet
                    </Button>
                  )}
                </Box>

              {selectedFlow ? (
                <Box>
                  {/* Flow Controls */}
                  <Paper 
                    sx={{ 
                      p: 3, 
                      mb: 2, 
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
                      border: '1px solid #90caf9'
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography variant="h6" sx={{ color: '#1565c0', fontWeight: 600 }}>
                          ⚙️ Akış Kontrolleri
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Bileşenleri düzenleyin ve akışı yönetin
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={handleReorderComponents}
                          disabled={!selectedFlow || selectedFlow.components.length < 2}
                          sx={{
                            background: 'linear-gradient(45deg, #4caf50 30%, #66bb6a 90%)',
                            boxShadow: '0 3px 5px 2px rgba(76, 175, 80, .3)',
                            '&:hover': {
                              background: 'linear-gradient(45deg, #388e3c 30%, #4caf50 90%)',
                            }
                          }}
                        >
                          🔄 Yeniden Sırala
                        </Button>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={handleClearFlow}
                          disabled={!selectedFlow || selectedFlow.components.length === 0}
                          sx={{
                            background: 'linear-gradient(45deg, #f44336 30%, #ef5350 90%)',
                            boxShadow: '0 3px 5px 2px rgba(244, 67, 54, .3)',
                            '&:hover': {
                              background: 'linear-gradient(45deg, #d32f2f 30%, #f44336 90%)',
                            }
                          }}
                        >
                          🗑️ Temizle
                        </Button>
                      </Box>
                    </Box>
                  </Paper>

                  {/* Component Palette */}
                  <Paper 
                    sx={{ 
                      p: 3, 
                      mb: 2, 
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                      border: '1px solid #dee2e6'
                    }}
                  >
                    <Typography variant="h6" gutterBottom sx={{ color: '#495057', fontWeight: 600 }}>
                      🧩 Bileşen Paleti
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Bileşenleri sürükleyip akış alanına bırakın
                    </Typography>
                    <Grid container spacing={2}>
                      {componentTemplates.map((template) => (
                        <Grid size={{ xs: 6, sm: 4, md: 3 }} key={template.type}>
                          <Paper
                            sx={{
                              p: 2,
                              textAlign: 'center',
                              cursor: 'grab',
                              border: '2px dashed #dee2e6',
                              borderRadius: 2,
                              transition: 'all 0.3s ease',
                              background: 'white',
                              '&:hover': {
                                borderColor: template.color,
                                backgroundColor: `${template.color}08`,
                                transform: 'translateY(-2px)',
                                boxShadow: `0 4px 12px ${template.color}30`
                              },
                              '&:active': {
                                cursor: 'grabbing',
                                transform: 'scale(0.98)'
                              }
                            }}
                            draggable
                            onDragStart={(e) => handleDragStart(e, template)}
                          >
                            <Box sx={{ color: template.color, mb: 1, fontSize: '24px' }}>
                              {template.icon}
                            </Box>
                            <Typography variant="body2" fontWeight="600" color="text.primary">
                              {template.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                              {template.description}
                            </Typography>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  </Paper>

                  {/* Flow Canvas */}
                  <Paper
                    className="flow-canvas"
                    sx={{
                      height: 600,
                      border: '2px dashed #e0e0e0',
                      position: 'relative',
                      overflow: 'auto',
                      background: 'linear-gradient(90deg, #f8f9fa 1px, transparent 1px), linear-gradient(#f8f9fa 1px, transparent 1px)',
                      backgroundSize: '20px 20px',
                      borderRadius: 2,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      '&:hover': {
                        borderColor: '#1976d2'
                      }
                    }}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                  >
                    {/* Connection Lines */}
                    <svg
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        pointerEvents: 'none',
                        zIndex: 1
                      }}
                    >
                      {selectedFlow.components.map((component, index) => {
                        const nextComponent = selectedFlow.components[index + 1];
                        if (!nextComponent) return null;
                        
                        const startX = component.position.x + 60; // Component center
                        const startY = component.position.y + 40;
                        const endX = nextComponent.position.x + 60;
                        const endY = nextComponent.position.y;
                        
                        return (
                          <line
                            key={`connection-${component.id}-${nextComponent.id}`}
                            x1={startX}
                            y1={startY}
                            x2={endX}
                            y2={endY}
                            stroke="#1976d2"
                            strokeWidth="2"
                            markerEnd="url(#arrowhead)"
                          />
                        );
                      })}
                      
                      {/* Arrow marker definition */}
                      <defs>
                        <marker
                          id="arrowhead"
                          markerWidth="10"
                          markerHeight="7"
                          refX="9"
                          refY="3.5"
                          orient="auto"
                        >
                          <polygon
                            points="0 0, 10 3.5, 0 7"
                            fill="#1976d2"
                          />
                        </marker>
                      </defs>
                    </svg>

                    {/* Flow Steps */}
                    {selectedFlow.components.map((component, index) => (
                      <Box
                        key={component.id}
                        draggable
                        onDragStart={(e) => handleFlowComponentDragStart(e, component)}
                        onDragEnd={handleFlowComponentDragEnd}
                        sx={{
                          position: 'absolute',
                          left: component.position.x,
                          top: component.position.y,
                          minWidth: 140,
                          p: 2,
                          backgroundColor: 'white',
                          border: '2px solid #1976d2',
                          borderRadius: 2,
                          cursor: 'grab',
                          zIndex: 2,
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            backgroundColor: '#f8f9fa',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 6px 20px rgba(0,0,0,0.2)',
                            borderColor: '#1565c0'
                          },
                          '&:active': {
                            cursor: 'grabbing',
                            transform: 'scale(0.98)'
                          }
                        }}
                        onClick={() => handleComponentClick(component)}
                      >
                        {/* Step Number */}
                        <Box
                          sx={{
                            position: 'absolute',
                            top: -12,
                            left: -12,
                            width: 28,
                            height: 28,
                            backgroundColor: '#1976d2',
                            color: 'white',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '13px',
                            fontWeight: 'bold',
                            boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)',
                            border: '2px solid white'
                          }}
                        >
                          {index + 1}
                        </Box>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                          <Box sx={{ color: getComponentColor(component.type), display: 'flex', alignItems: 'center' }}>
                            {componentTemplates.find(t => t.type === component.type)?.icon}
                          </Box>
                          <Typography variant="body2" fontWeight="600" color="text.primary">
                            {component.name}
                          </Typography>
                        </Box>
                        
                        {/* Component Type Badge */}
                        <Box
                          sx={{
                            position: 'absolute',
                            top: -8,
                            right: -8,
                            backgroundColor: getComponentColor(component.type),
                            color: 'white',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 1.5,
                            fontSize: '10px',
                            fontWeight: 'bold',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}
                        >
                          {getComponentTypeName(component.type)}
                        </Box>
                        
                        {/* Drag Handle */}
                        <Box
                          sx={{
                            position: 'absolute',
                            bottom: 4,
                            right: 4,
                            color: '#999',
                            opacity: 0.6,
                            '&:hover': {
                              opacity: 1
                            }
                          }}
                        >
                          <IconGripVertical size={16} />
                        </Box>
                      </Box>
                    ))}
                    
                    {/* Start Indicator */}
                    {selectedFlow.components.length > 0 && (
                      <Box
                        sx={{
                          position: 'absolute',
                          left: selectedFlow.components[0].position.x + 60,
                          top: selectedFlow.components[0].position.y - 40,
                          backgroundColor: '#4caf50',
                          color: 'white',
                          px: 2.5,
                          py: 1.5,
                          borderRadius: 2,
                          fontSize: '12px',
                          fontWeight: 'bold',
                          zIndex: 3,
                          boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)',
                          border: '2px solid white',
                          textTransform: 'uppercase',
                          letterSpacing: '1px'
                        }}
                      >
                        🚀 Başlangıç
                      </Box>
                    )}
                    
                    {/* End Indicator */}
                    {selectedFlow.components.length > 0 && (
                      <Box
                        sx={{
                          position: 'absolute',
                          left: selectedFlow.components[selectedFlow.components.length - 1].position.x + 60,
                          top: selectedFlow.components[selectedFlow.components.length - 1].position.y + 80,
                          backgroundColor: '#f44336',
                          color: 'white',
                          px: 2.5,
                          py: 1.5,
                          borderRadius: 2,
                          fontSize: '12px',
                          fontWeight: 'bold',
                          zIndex: 3,
                          boxShadow: '0 4px 12px rgba(244, 67, 54, 0.3)',
                          border: '2px solid white',
                          textTransform: 'uppercase',
                          letterSpacing: '1px'
                        }}
                      >
                        🏁 Son
                      </Box>
                    )}
                  </Paper>
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="h6" color="text.secondary">
                    IVR akışı oluşturmak için yukarıdan bir akış seçin
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Flow Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingFlow ? 'IVR Akışını Düzenle' : 'Yeni IVR Akışı'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Akış Adı"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Açıklama"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <FormControl fullWidth>
                  <InputLabel>SIP Numarası</InputLabel>
                  <Select
                    value={formData.sipNumberId}
                    onChange={(e) => setFormData({ ...formData, sipNumberId: e.target.value })}
                    required
                  >
                    {sipNumberContext.sipNumbers.map((sip) => (
                      <MenuItem key={sip._id} value={sip._id}>
                        {sip.name} - {sip.number}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>İptal</Button>
            <Button type="submit" variant="contained" disabled={loading}>
              {loading ? <CircularProgress size={20} /> : 'Kaydet'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Component Config Dialog */}
      <Dialog 
        open={componentConfigOpen} 
        onClose={() => setComponentConfigOpen(false)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          {selectedComponent?.name} - Yapılandırma
        </DialogTitle>
        <DialogContent>
          {selectedComponent && (
            <ComponentConfig
              component={selectedComponent}
              onConfigChange={updateComponentConfig}
              queues={queueContext.queues}
              musicFiles={musicFileContext.musicFiles}
              onUploadMusicFile={musicFileContext.uploadMusicFile}
              musicLoading={musicFileContext.loading}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setComponentConfigOpen(false)}>Kapat</Button>
        </DialogActions>
      </Dialog>
    </Box>
    </Box>
  );
};

export default IvrBuilder;